import Label from '../../../components/Label';

// ----------------------------------------------------------------------

/**
 * Puts the counts from useNavBadges onto the nav config.
 *
 * An item names its number with `badgeSource`; the count arrives keyed the
 * same way. A parent shows the total of its children, because a badge sitting
 * under a collapsed group is a badge nobody sees, and the group is collapsed
 * most of the time.
 *
 * The config itself is never mutated. It is a module-level constant shared by
 * every render of the nav, and writing counts into it would leave last
 * session's numbers behind on a re-login.
 */

const badgeLabel = (count) => (
  <Label variant="filled" color="error" sx={{ height: 20, minWidth: 20, px: 0.75 }}>
    {count > 99 ? '99+' : count}
  </Label>
);

const decorate = (item, counts) => {
  const children = item.children?.map((child) => decorate(child, counts));

  const own = item.badgeSource ? counts[item.badgeSource] || 0 : 0;
  const inherited = children
    ? children.reduce((sum, child) => sum + (child.badgeCount || 0), 0)
    : 0;
  const count = own + inherited;

  if (!count && !children) return item;

  return {
    ...item,
    ...(children && { children }),
    ...(count && { badgeCount: count, info: badgeLabel(count) }),
  };
};

export default function applyNavBadges(navList, counts) {
  if (!counts || !Object.values(counts).some(Boolean)) return navList;
  return navList.map((group) => ({
    ...group,
    items: group.items.map((item) => decorate(item, counts)),
  }));
}
